import { Cylinder } from "lucide-react";
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { maskVinNumber } from "../utils/functions";
import { useCurrencyUtils } from "../utils/currencyUtils";
import api from "../lib/api.ts";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";
import BidInformation from "./BidInformation";
import placeholder from "../assets/Car-place-holder-PNG.png";

const VehicleListCard = ({ vehicle }) => {
  const {
    id,
    name,
    title: propTitle,
    slug,
    lotNumber,
    lotNo: propLotNo,
    vin,
    primaryDamageType: condition,
    mileage,
    miles: propMiles,
    cylinder,
    fuelType,
    engineSize,
    startingBid: startBidding,
    latestBid,
    image: propImage,
    isRunAndDrive = false,
    sellerName = "",
    highlights: propHighlights,
    auction: propAuction,
  } = vehicle || {};

  const title = name || propTitle || "Unknown Vehicle";
  const lotNo = propLotNo || lotNumber;
  const miles = propMiles || mileage || vehicle?.miles || 0;
  const imageUrls = (vehicle?.images || [])
    .map((img) =>
      typeof img === "string"
        ? img
        : img?.imageUrl || img?.url || img?.path || img?.src || "",
    )
    .filter(Boolean);
  const image = propImage || vehicle?.imageUrl || (imageUrls.length > 0 ? imageUrls[0] : placeholder);
  const runNo = vehicle.runNo || vehicle.auctionLanesVehicles?.[0]?.runNo;
  const auctionLaneId = vehicle.auctionLanesVehicles?.[0]?.auctionLane?.id;
  const bidIncrement = vehicle.bidIncrement || 100;
  const auction = propAuction || vehicle.auction || vehicle.auctionLanesVehicles?.[0]?.auctionLane?.auction;

  const rawHighlights = Array.isArray(propHighlights) ? propHighlights : (vehicle.highlight ? [vehicle.highlight] : []);
  const highlights = rawHighlights; // highlight map logic is already in the JSX for VehicleListCard

  const navigate = useNavigate();
  const { user } = useAuth();
  const { showSuccess, showError } = useToast();

  // currency conversion
  const { displayPriceWithSymbol, convertPrice } = useCurrencyUtils();

  // Check if vehicle is already sold
  const isVehicleSold =
    vehicle?.ProductStatus?.name === "Sold" ||
    vehicle?.ProductStatus === "Sold" ||
    vehicle?.soldPrice ||
    vehicle?.soldTo ||
    vehicle?.ProductAuctionStatus?.name === "Sold" ||
    vehicle?.ProductAuctionStatus === "Sold";

  // Prebid countdown timer state
  const [prebidTimeLeft, setPrebidTimeLeft] = useState(() => {
    if (
      !vehicle?.auctionLanesVehicles?.[0]?.auctionLane?.auction?.preBidEndDate
    )
      return 0;
    const start = new Date(
      vehicle.auctionLanesVehicles[0].auctionLane.auction.preBidEndDate
    );
    const now = new Date();
    return Math.max(0, start.getTime() - now.getTime());
  });

  // State for prebid popup
  const [showBidNowPopup, setShowBidNowPopup] = useState(false);
  const [bidAmount, setBidAmount] = useState(() => {
    // If current bid exists, use current bid + increment
    // Otherwise, use starting bid + increment
    const baseAmount = latestBid > 0 ? latestBid : startBidding;
    return baseAmount + (bidIncrement || 100);
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [updatedVehicle, setUpdatedVehicle] = useState(vehicle);

  // Update updatedVehicle when vehicle prop changes
  useEffect(() => {
    setUpdatedVehicle(vehicle);
  }, [vehicle]);

  // Timer effect for prebid countdown
  useEffect(() => {
    const timer = setInterval(() => {
      setPrebidTimeLeft((prevTime) => {
        if (prevTime <= 1000) {
          return 0;
        }
        return prevTime - 1000;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Handle bid now button click - show prebid popup
  const handleBidNow = () => {
    setShowBidNowPopup(true);
    // Set bid amount to current bid + increment, or starting bid + increment if no current bid
    // Use updatedVehicle to get the most current bid information
    const currentLatestBid = parseFloat(updatedVehicle.latestBid) || 0;
    const baseAmount = currentLatestBid > 0 ? currentLatestBid : startBidding;
    setBidAmount(baseAmount + (bidIncrement || 100));
  };

  // Close prebid popup
  const closeBidNowPopup = () => {
    setShowBidNowPopup(false);
    setBidAmount("");
  };

  const handleBidNowSubmit = async () => {
    setIsSubmitting(true);
    try {
      // Try to get auction lane ID from multiple sources
      let finalAuctionLaneId = auctionLaneId;

      if (
        !finalAuctionLaneId &&
        vehicle.auctionLanesVehicles?.[0]?.auctionLane?.id
      ) {
        finalAuctionLaneId = vehicle.auctionLanesVehicles[0].auctionLane.id;
      }

      if (!finalAuctionLaneId && vehicle.auctionLanesVehicles?.[0]?.id) {
        finalAuctionLaneId = vehicle.auctionLanesVehicles[0].id;
      }

      // Validate required fields
      if (!finalAuctionLaneId) {
        showError("Auction Lane ID is missing. Cannot submit bid now.");
        return;
      }

      if (!id) {
        showError("Vehicle ID is missing. Cannot submit bid now.");
        return;
      }

      if (!bidAmount || bidAmount <= 0) {
        showError("Please enter a valid bid amount.");
        return;
      }

      const response = await api.post("/bidding/prebid", {
        auctionLaneId: finalAuctionLaneId,
        vehicleId: id,
        amount: bidAmount,
      });

      if (response.data.success) {
        // Calculate next bid amount
        const currentBid = parseFloat(bidAmount);
        const increment = parseFloat(bidIncrement) || 100;
        const nextBidAmount = currentBid + increment;
        setBidAmount(nextBidAmount.toString());

        // Update the vehicle object with the new bid amount and set current user as highest bidder
        setUpdatedVehicle((prevVehicle) => ({
          ...prevVehicle,
          latestBid: currentBid.toString(),
          highestBidderId: user?.id,
        }));

        showSuccess("Bid now submitted successfully!");
      } else {
        showError(
          response.data.message || "Failed to submit bid now. Please try again."
        );
      }
    } catch (error) {
      console.error("Error submitting bid now:", error);
      const errorMessage =
        error.response?.data?.message ||
        "Failed to submit bid now. Please try again.";
      showError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Toggle wishlist item
  const toggleWishlist = async (vehicleId) => {
    if (!user?.id) {
      navigate("/login");
      return;
    }

    try {
      const response = await api.post("/wishlist/toggle", { vehicleId });

      if (response.data.success) {
        showSuccess("Vehicle added to wishlist successfully!");
      } else {
        showError("Failed to update wishlist. Please try again.");
      }
    } catch (error) {
      console.error("Error toggling wishlist:", error);
      showError("Failed to update wishlist. Please try again.");
    }
  };

  return (
    <>
      <div className='flex flex-col sm:grid sm:grid-cols-8 gap-2 sm:gap-2 mb-3 md:mb-5 shadow-lg hover:bg-gray-200 transition-all duration-300 items-center p-3 sm:p-2 md:p-0'>
        <div className='w-full sm:col-span-1 flex-shrink-0 cursor-pointer' onClick={() => navigate(`/${slug}`)}>
          <img
            src={image}
            alt={title}
            className='w-full h-48 sm:h-auto object-cover rounded'
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = placeholder;
            }}
          />
        </div>
        <div className='sm:border-r sm:border-gray-200 flex-1 sm:col-span-3 p-2 sm:p-1 md:p-0 min-w-0 mt-2 sm:mt-0 w-full'>
          <h3 className='font-bold text-orange-500 text-sm sm:text-xs md:text-smd mb-2 sm:mb-1 truncate cursor-pointer hover:text-orange-600 transition-colors duration-200' onClick={() => navigate(`/${slug}`)}>
            {title}
          </h3>
          {runNo && (
            <span className='text-xs md:text-sm text-gray-600 block'>
              Run #  <span className=''>{runNo}</span>
            </span>
          )}
          <span className='text-xs md:text-sm text-gray-600 block'>
            Lot # <span className=''>{lotNo}</span>
          </span>
          <span className='text-xs md:text-sm text-gray-600 block'>
            Vin # <span className=''>{maskVinNumber(vin)}</span>
          </span>
          {/* Share and Bookmark Icons - Side by side */}
          <div className='flex gap-1 justify-left items-center mt-1'>
            <button
              className='group relative flex items-center justify-center text-[#F15A29] shadow-lg overflow-hidden transition-all duration-300 border border-black'
              style={{
                width: "20px",
                height: "20px",
                borderRadius: "25px",
                backgroundColor: "transparent",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.width = "130px";
                e.currentTarget.style.borderRadius = "25px";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.width = "50px";
                e.currentTarget.style.borderRadius = "25px";
              }}
              onClick={() => toggleWishlist(id)}
            >
              <span className='absolute inset-0 before:absolute before:bottom-[-100%] before:left-0 before:w-full before:h-full before:bg-gradient-to-t before:from-[#ff4800] before:via-[#f1a129] before:to-[#ff74025d] before:transition-all before:duration-700 before:ease-in-out group-hover:before:bottom-0'></span>
              <span className='absolute opacity-100 group-hover:opacity-0 transition duration-300 z-10'>
                <svg
                  width='12'
                  height='12'
                  viewBox='0 0 15 16'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                >
                  <path
                    d='M11.8339 16C11.2212 16 10.702 15.7828 10.2762 15.3485C9.85038 14.9141 9.63745 14.3845 9.63745 13.7597C9.63745 13.6505 9.67014 13.4414 9.73551 13.1325L3.78745 9.56528C3.58851 9.77594 3.35259 9.94221 3.07969 10.0641C2.80666 10.186 2.51415 10.2469 2.20216 10.2469C1.59774 10.2469 1.084 10.0288 0.660948 9.59239C0.237894 9.15588 0.0263672 8.62831 0.0263672 8.00968C0.0263672 7.39105 0.238105 6.8614 0.66158 6.42074C1.08506 5.98007 1.59929 5.75973 2.20427 5.75973C2.51696 5.75973 2.80827 5.8191 3.07822 5.93784C3.34816 6.05671 3.58106 6.22098 3.77691 6.43063L9.73551 2.86986C9.69938 2.76791 9.674 2.66616 9.65938 2.56464C9.64476 2.46311 9.63745 2.35362 9.63745 2.23618C9.63745 1.61497 9.85038 1.08697 10.2762 0.652183C10.7021 0.217395 11.2207 0 11.832 0C12.4433 0 12.9618 0.217681 13.3876 0.653043C13.8134 1.08841 14.0264 1.61705 14.0264 2.23898C14.0264 2.86406 13.8132 3.39435 13.3867 3.82986C12.9603 4.26536 12.4425 4.48311 11.8333 4.48311C11.52 4.48311 11.2291 4.42145 10.9604 4.29813C10.6918 4.17495 10.458 4.00437 10.2587 3.78641L4.31068 7.35384C4.34414 7.46411 4.36889 7.57202 4.38492 7.67757C4.4008 7.78296 4.40875 7.89223 4.40875 8.00538C4.40875 8.11866 4.40256 8.22542 4.39019 8.32566C4.37768 8.4259 4.35469 8.52872 4.32123 8.63412L10.2587 12.2093C10.4567 11.9942 10.6903 11.8243 10.9595 11.6997C11.2289 11.5751 11.5202 11.5128 11.8333 11.5128C12.4425 11.5128 12.9603 11.7314 13.3867 12.1686C13.8132 12.6059 14.0264 13.1352 14.0264 13.7565C14.0264 14.3836 13.8132 14.9142 13.3869 15.3485C12.9606 15.7828 12.443 16 11.8339 16ZM11.8318 14.7963C12.1211 14.7963 12.3626 14.6976 12.556 14.5003C12.7495 14.303 12.8462 14.0568 12.8462 13.7617C12.8462 13.4697 12.7492 13.2225 12.5552 13.02C12.3611 12.8177 12.1207 12.7165 11.8339 12.7165C11.5433 12.7165 11.3013 12.818 11.1078 13.0209C10.9143 13.2239 10.8176 13.4702 10.8176 13.7597C10.8176 14.0531 10.9143 14.2993 11.1078 14.4982C11.3013 14.6969 11.5426 14.7963 11.8318 14.7963ZM2.20427 9.04323C2.49151 9.04323 2.73404 8.94458 2.93186 8.74726C3.12968 8.54994 3.22859 8.30372 3.22859 8.0086C3.22859 7.71664 3.13045 7.46942 2.93418 7.26694C2.73776 7.0646 2.49446 6.96343 2.20427 6.96343C1.92153 6.96343 1.68456 7.06489 1.49335 7.2678C1.30214 7.47085 1.20653 7.71714 1.20653 8.00667C1.20653 8.29734 1.30214 8.54277 1.49335 8.74296C1.68456 8.94314 1.92153 9.04323 2.20427 9.04323ZM11.8318 3.27941C12.1211 3.27941 12.3626 3.17889 12.556 2.97784C12.7495 2.7768 12.8462 2.53029 12.8462 2.23833C12.8462 1.94623 12.7492 1.70072 12.5552 1.50183C12.3611 1.30308 12.1207 1.2037 11.8339 1.2037C11.5433 1.2037 11.3013 1.30265 11.1078 1.50054C10.9143 1.69843 10.8176 1.94364 10.8176 2.23618C10.8176 2.52958 10.9143 2.7768 11.1078 2.97784C11.3013 3.17889 11.5426 3.27941 11.8318 3.27941Z'
                    fill='#111111'
                    fillOpacity='0.8'
                  />
                </svg>
              </span>
              <span className='opacity-0 group-hover:opacity-100 transition duration-300 font-medium text-white z-10'>
                SHARE
              </span>
            </button>

            <button
              className='group relative flex items-center justify-center text-[#F15A29] shadow-lg overflow-hidden transition-all duration-300 border border-black'
              style={{
                width: "20px",
                height: "20px",
                borderRadius: "25px",
                backgroundColor: "transparent",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.width = "130px";
                e.currentTarget.style.borderRadius = "25px";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.width = "50px";
                e.currentTarget.style.borderRadius = "25px";
              }}
              onClick={() => toggleWishlist(id)}
            >
              <span className='absolute inset-0 before:absolute before:bottom-[-100%] before:left-0 before:w-full before:h-full before:bg-gradient-to-t before:from-[#ff4800] before:via-[#f1a129] before:to-[#ff74025d] before:transition-all before:duration-700 before:ease-in-out group-hover:before:bottom-0'></span>
              <span className='absolute opacity-100 group-hover:opacity-0 transition duration-300 z-10'>
                <svg
                  width='10'
                  height='10'
                  viewBox='0 0 18 18'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                >
                  <path
                    d='M14.1005 18C13.7405 18 13.4005 17.9 13.0805 17.7L9.18047 15.24C9.06047 15.16 8.94047 15.16 8.82047 15.24L4.92047 17.7C4.56047 17.94 4.16047 18.03 3.72047 17.97C3.28047 17.91 2.90047 17.74 2.58047 17.46C2.18047 17.1 1.98047 16.64 1.98047 16.08V1.92C1.98047 1.4 2.17047 0.950001 2.55047 0.57C2.93047 0.190001 3.38047 0 3.90047 0H14.1005C14.6205 0 15.0705 0.190001 15.4505 0.57C15.8305 0.950001 16.0205 1.4 16.0205 1.92V16.08C16.0205 16.6 15.8305 17.05 15.4505 17.43C15.0705 17.81 14.6205 18 14.1005 18ZM9.00047 13.62C9.36047 13.62 9.70047 13.72 10.0205 13.92L13.9205 16.38C13.9605 16.42 14.0205 16.44 14.1005 16.44C14.1805 16.44 14.2505 16.41 14.3105 16.35C14.3705 16.29 14.4005 16.2 14.4005 16.08V1.92C14.4005 1.8 14.3705 1.71 14.3105 1.65C14.2505 1.59 14.1805 1.56 14.1005 1.56H3.90047C3.82047 1.56 3.75047 1.59 3.69047 1.65C3.63047 1.71 3.60047 1.8 3.60047 1.92V16.08C3.60047 16.2 3.65047 16.3 3.75047 16.38C3.85047 16.46 3.96047 16.46 4.08047 16.38L7.98047 13.92C8.30047 13.72 8.64047 13.62 9.00047 13.62Z'
                    fill='#111111'
                    fillOpacity='0.8'
                  />
                </svg>
              </span>
              <span className='opacity-0 group-hover:opacity-100 transition duration-300 font-medium text-white z-10'>
                Save
              </span>
            </button>
          </div>
        </div>
        <div className='block items-center sm:border-r sm:border-gray-200 p-2 sm:p-1 md:p-2 text-xs md:text-sm col-span-1 md:col-span-1 mt-2 sm:mt-0 w-full'>
          <div className='text-[#000000] flex justify-between border-b border-gray-200 py-1.5 sm:py-1'>
            <span className='font-medium'>Miles:</span>
            <span className='text-[#414042]'>{miles}</span>
          </div>

          <div className='text-[#000000] flex justify-between border-b border-gray-200 py-1.5 sm:py-1'>
            <span className='font-medium'>Fuel:</span>
            <span className='text-[#414042]'>{fuelType}</span>
          </div>

          <div className='text-[#000000] flex justify-between border-b border-gray-200 py-1.5 sm:py-1'>
            <span className='font-medium'>Cylinder:</span>
            <span className='text-[#414042]'>{cylinder}</span>
          </div>

          <div className='text-[#000000] flex justify-between py-1.5 sm:py-1'>
            <span className='font-medium'>Engine:</span>
            <span className='text-[#414042]'>{engineSize}</span>
          </div>
        </div>
        <div className='hidden sm:block border-r border-gray-200 p-1 md:p-2 col-span-1 md:col-span-1'>
          {/* Highlights Tags */}
          {highlights.length > 0 && (
            <div className='max-w-3/4 gap-1 flex flex-col md:flex-col'>
              {highlights.slice(0, 2).map((highlight, index) => {
                const isObject =
                  typeof highlight === "object" && highlight !== null;
                const name = isObject ? highlight.name : highlight;
                const bgColor = isObject ? highlight.bgColor : "#f97316"; // default orange
                const textColor = isObject ? highlight.textColor : "#ffffff"; // default white

                return (
                  <div
                    key={index}
                    className='px-1 md:px-2 py-0.5 md:py-1 rounded text-xs font-semibold text-center shadow-sm mb-1'
                    style={{
                      backgroundColor: bgColor,
                      color: textColor,
                    }}
                  >
                    {name}
                  </div>
                );
              })}
              {highlights.length > 2 && (
                <div className='bg-gray-600 text-white px-1 md:px-2 py-0.5 md:py-1 rounded text-xs font-semibold text-center shadow-sm'>
                  +{highlights.length - 2}
                </div>
              )}
            </div>
          )}
        </div>
        {/* <div className="border-r border-gray-200 p-2">
        {" "}
        <span className="text-sm text-gray-600 text-center">
          <span className="font-normal block">Seller Name</span>
          <span className="font-semibold block">{sellerName}</span>
        </span>
      </div> */}
        <div className='text-center sm:border-r sm:border-gray-200 p-3 sm:p-1 md:p-10 flex-1 sm:col-span-1 min-w-0 mt-2 sm:mt-0 w-full'>
          {/* Right Content - Bidding Info */}
          <div className='flex flex-col items-center sm:items-start'>
            {/* Auction Status */}
            <div className='mb-2 sm:mb-1 md:mb-2 w-full'>
              <div className='text-orange-500 font-semibold text-sm sm:text-xs md:text-sm mb-1'>
                Auction In
              </div>
              <div className='text-xs sm:text-xs text-gray-600 mb-1'>
                Starting Bid{" "}
                <span>
                  {displayPriceWithSymbol(convertPrice(startBidding))}
                </span>
              </div>
              <div className='text-xs sm:text-xs text-gray-600'>
                Current Bid{" "}
                <span className='text-orange-600 font-bold text-base sm:text-sm md:text-lg'>
                  {displayPriceWithSymbol(convertPrice(latestBid))}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div className='sm:border-r sm:border-gray-200 p-3 sm:p-1 md:p-10 flex-shrink-0 sm:col-span-1 mt-2 sm:mt-0 w-full'>
          {/* Action Buttons */}
          <div className='flex flex-col sm:flex-col gap-2 sm:space-y-1 md:space-y-2 w-full'>
            {isVehicleSold ? (
              <div className='px-4 sm:px-2 md:px-4 py-2.5 sm:py-1 md:py-2 bg-red-600 text-white rounded cursor-not-allowed text-sm sm:text-xs md:text-sm text-center font-medium w-full'>
                Vehicle Sold
              </div>
            ) : (
              <button
                onClick={handleBidNow}
                className='px-4 sm:px-2 md:px-4 py-2.5 sm:py-1 md:py-2 bg-orange-500 text-white rounded hover:bg-orange-600 transition-colors duration-200 text-sm sm:text-xs md:text-sm font-medium w-full'
              >
                Pre-Bid
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Prebid Popup Modal */}
      {showBidNowPopup && (
        <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50'>
          <div className='bg-white rounded-lg p-6 max-w-md w-full mx-4'>
            {/* Header */}
            <div className='flex justify-between items-center mb-4'>
              <h3 className='text-lg font-bold text-gray-900'>
                Pre-Bid Summary
              </h3>
              <button
                onClick={closeBidNowPopup}
                className='text-gray-400 hover:text-gray-600'
              >
                <svg
                  className='w-6 h-6'
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M6 18L18 6M6 6l12 12'
                  />
                </svg>
              </button>
            </div>

            {/* Vehicle Info */}
            <div className='mb-4'>
              <h4 className='font-semibold text-gray-900 mb-2'>{title}</h4>
              <div className='flex justify-between text-sm text-gray-600'>
                <span>
                  Lot No:{" "}
                  <span className='font-semibold text-orange-600'>{lotNo}</span>
                </span>
                {runNo && (
                  <span>
                    Run No:{" "}
                    <span className='font-semibold text-orange-600'>
                      {runNo}
                    </span>
                  </span>
                )}
              </div>
            </div>

            {/* Bid Information Card */}
            <BidInformation
              vehicle={updatedVehicle}
              className='mb-4'
              prebidTimeLeft={prebidTimeLeft}
              bidAmount={bidAmount}
              setBidAmount={setBidAmount}
              bidIncrement={bidIncrement}
              bidMessage=''
              isSubmittingBid={isSubmitting}
              handlePreBidSubmit={handleBidNowSubmit}
              getMinBidAmount={() => {
                // Minimum bid should be current bid + increment, or starting bid if no current bid
                const currentLatestBid =
                  parseFloat(updatedVehicle.latestBid) || 0;
                return currentLatestBid > 0
                  ? currentLatestBid + bidIncrement
                  : startBidding;
              }}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default VehicleListCard;
